import "@/App.css";
import { Component, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { TourProvider } from "@/context/TourContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AppLayout from "@/components/layout/AppLayout";
import CookieBanner from "@/components/CookieBanner";
import { ADMIN_LOGIN_PATH } from "@/lib/adminRoutes";

// Public / eagerly loaded
import Login from "@/pages/Login";
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const PublicRsvp = lazy(() => import("@/pages/PublicRsvp"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const TeamAccept = lazy(() => import("@/pages/TeamAccept"));

// Legal pages (public, no auth)
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Dpa = lazy(() => import("@/pages/Dpa"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));

// App pages
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const Clients = lazy(() => import("@/pages/Clients"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const Settings = lazy(() => import("@/pages/Settings"));
const Guide = lazy(() => import("@/pages/Guide"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const PlansPage = lazy(() => import("@/pages/PlansPage"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
          <div style={{ textAlign: "center", color: "#a1a1aa", fontFamily: "monospace", maxWidth: 480 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 16, color: "#ef4444" }}>
              Application Error
            </div>
            <div style={{ fontSize: 13, marginBottom: 12, color: "#fafafa" }}>
              Impossibile aprire questa schermata. Ricarica per riprovare.
            </div>
            <div style={{ fontSize: 11, color: "#52525b", marginBottom: 24 }}>
              Se il problema persiste, contatta il supporto.
            </div>
            <button
              onClick={() => { window.location.reload(); }}
              style={{ background: "#27272a", border: "1px solid #3f3f46", color: "#d4d4d8", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontSize: 12 }}
            >
              Ricarica
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={200} skipDelayDuration={100}>
            <BrowserRouter>
              <Suspense fallback={<div role="status" className="p-6">Caricamento… / Loading…</div>}>
              <Routes>

                {/* ── Public routes (no auth required) ── */}
                <Route path="/"             element={<Navigate to="/login" replace />} />
                <Route path="/login"        element={<Login />} />
                <Route path="/register"     element={<Register />} />
                <Route path={ADMIN_LOGIN_PATH} element={<AdminLogin />} />
                <Route path="/admin-login" element={<Navigate to={ADMIN_LOGIN_PATH} replace />} />
                {/* Recupero password per planner, collaboratori e admin. */}
                <Route path="/password-dimenticata"     element={<ForgotPassword />} />
                <Route path="/reimposta-password/:token" element={<ResetPassword />} />
                <Route path="/rsvp/:id"           element={<PublicRsvp />} />
                <Route path="/portal/:token"      element={<ClientPortal />} />
                <Route path="/team/accept/:token" element={<TeamAccept />} />

                {/* ── Legal pages (public, no auth) ── */}
                <Route path="/privacy"  element={<PrivacyPolicy />} />
                <Route path="/terms"    element={<TermsOfService />} />
                <Route path="/dpa"      element={<Dpa />} />
                <Route path="/cookies"  element={<CookiePolicy />} />

                {/* ── Semi-protected (auth required, subscription optional) ── */}
                <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
                <Route path="/onboarding"      element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/plans"           element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />

                {/* ── Admin panel — secret path, role=admin only ── */}
                <Route
                  path="/gz60k37x-niiwy0kb-ymps2jw4-tjus19xm"
                  element={<AdminRoute><AdminPanel /></AdminRoute>}
                />

                {/* ── Planner app (subscription required) ── */}
                <Route element={<ProtectedRoute><TourProvider><AppLayout /></TourProvider></ProtectedRoute>}>
                  <Route path="/dashboard"     element={<Dashboard />} />
                  <Route path="/events"        element={<Events />} />
                  <Route path="/events/:id"    element={<EventDetail />} />
                  <Route path="/clients"       element={<Clients />} />
                  <Route path="/clients/:id"   element={<ClientDetail />} />
                  <Route path="/calendar"      element={<Calendar />} />
                  <Route path="/suppliers"     element={<Suppliers />} />
                  <Route path="/guida"         element={<Guide />} />
                  <Route path="/settings"      element={<Settings />} />
                </Route>

                {/* ── Catch-all → landing page ── */}
                <Route path="*" element={<Navigate to="/" replace />} />

              </Routes>
              </Suspense>

              {/* Cookie banner — visibile su tutte le pagine finché non si esprime consenso */}
              <CookieBanner />
              <Toaster position="top-right" />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
